import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, Bars3Icon } from '@heroicons/react/24/outline';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ColumnConfig, ListLayoutEntityType, ENTITY_LABELS } from '../config/listColumns';

interface SortableColumnItemProps {
  column: ColumnConfig;
  onToggleVisibility: (key: string) => void;
}

const SortableColumnItem: React.FC<SortableColumnItemProps> = ({
  column,
  onToggleVisibility,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center p-3 bg-gray-50 rounded-lg ${
        isDragging ? 'shadow-lg' : ''
      }`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 focus:outline-none mr-3"
      >
        <Bars3Icon className="h-5 w-5" />
      </button>
      <label className="flex items-center flex-1 cursor-pointer">
        <input
          type="checkbox"
          checked={column.visible}
          onChange={() => onToggleVisibility(column.key)}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <span className="ml-3 text-sm text-gray-900">{column.label}</span>
      </label>
    </div>
  );
};

interface ListLayoutSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: ListLayoutEntityType;
  columns: ColumnConfig[];
  onSave: (columns: ColumnConfig[]) => Promise<void> | void;
  onReset: () => Promise<void> | void;
  isSaving?: boolean;
  isResetting?: boolean;
}

const ListLayoutSettingsModal: React.FC<ListLayoutSettingsModalProps> = ({
  isOpen,
  onClose,
  entityType,
  columns,
  onSave,
  onReset,
  isSaving = false,
  isResetting = false,
}) => {
  const [localColumns, setLocalColumns] = useState<ColumnConfig[]>([]);

  // Sync local state when modal opens or columns change
  useEffect(() => {
    if (isOpen) {
      setLocalColumns([...columns].sort((a, b) => a.order - b.order));
    }
  }, [isOpen, columns]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setLocalColumns((items) => {
        const oldIndex = items.findIndex((item) => item.key === active.id);
        const newIndex = items.findIndex((item) => item.key === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);
        // Update order values
        return newItems.map((item, index) => ({ ...item, order: index }));
      });
    }
  };

  const handleToggleVisibility = (key: string) => {
    setLocalColumns((prev) =>
      prev.map((col) =>
        col.key === key ? { ...col, visible: !col.visible } : col
      )
    );
  };

  const handleSave = async () => {
    try {
      await onSave(localColumns);
      onClose();
    } catch (error) {
      console.error('Failed to save list layout:', error);
      // Don't close on error
    }
  };

  const handleReset = async () => {
    try {
      await onReset();
      onClose();
    } catch (error) {
      console.error('Failed to reset list layout:', error);
      // Don't close on error
    }
  };

  const entityLabel = ENTITY_LABELS[entityType];
  const isLoading = isSaving || isResetting;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-start mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium text-gray-900"
                  >
                    {entityLabel} List Settings
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                    disabled={isLoading}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <p className="text-sm text-gray-500 mb-4">
                  Toggle column visibility and drag to reorder.
                </p>

                <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={localColumns.map((c) => c.key)}
                      strategy={verticalListSortingStrategy}
                    >
                      {localColumns.map((column) => (
                        <SortableColumnItem
                          key={column.key}
                          column={column}
                          onToggleVisibility={handleToggleVisibility}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </div>

                <div className="mt-6 flex justify-between">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
                    disabled={isLoading}
                  >
                    {isResetting ? 'Resetting...' : 'Reset to Default'}
                  </button>
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                      disabled={isLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSave}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                      disabled={isLoading}
                    >
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ListLayoutSettingsModal;
